'use client';

import Socials from "@/components/Socials";
import { useBranchCode } from '@/components/SiteHostProvider';
import { usePathname } from 'next/navigation';
import { getAllBranches, regionalizePath } from '@/lib/config/branches-config';
import { rememberRegionChoice } from '@/lib/geo/geo-hint';
import { useLanguage } from '@/lib/config/useLanguage';
import { isGlobalContentPath, normalizeGlobalContentPath } from '@/lib/config/global-content';

const SBIS_RELIABILITY_URL = 'https://saby.ru/profile/7814608295-780601001';

function FooterCom({info, sections, legal, socials = []}) {
    const currentYear = new Date().getFullYear();
    const pathname = usePathname() || '/';
    // Филиал страницы: по хосту (BRANCH_ROUTING=host) или по /geo/<code>/.
    const pageBranchCode = useBranchCode();
    const currentBranchCode = pageBranchCode;
    const { localizePath } = useLanguage();
    
    // Функция для комбинирования языковой и региональной локализации
    const localizeAndRegionalizePath = (path) => {
        // Если путь уже является внешней ссылкой или якорной ссылкой, возвращаем как есть
        if (!path || path.startsWith('http://') || path.startsWith('https://') || path.startsWith('//') || path.startsWith('#')) {
            return path;
        }
        const canonicalPath = normalizeGlobalContentPath(path);
        const localizedPath = localizePath(canonicalPath);
        if (isGlobalContentPath(canonicalPath)) {
            // Глобальные страницы живут в основном филиале; при BRANCH_ROUTING=host
            // с поддомена филиала это абсолютная ссылка на главный хост.
            return regionalizePath(localizedPath, 'spb', currentBranchCode);
        }
        return regionalizePath(localizedPath, currentBranchCode);
    };

    // Тот же путь в каждом регионе: regionalizePath сначала снимает существующий
    // префикс /geo/xx, поэтому ссылки корректны в обе стороны.
    const branchLinks = getAllBranches().map((branch) => ({
        code: branch.code,
        name: branch.name,
        // В режиме BRANCH_ROUTING=host — абсолютный URL того же пути на хосте филиала.
        href: regionalizePath(pathname, branch.code, currentBranchCode),
        isCurrent: branch.code === currentBranchCode,
    }));
    return (
        <>
            <div className="mt-10 grid grid-cols-1 gap-x-8 gap-y-10 sm:grid-cols-2 lg:grid-cols-6">
                <div className="col-span-2 lg:col-span-2">
                    <h3 className="text-xl font-semibold text-white">
                        {info.title}
                    </h3>
                    <p className={'text-sm/8 text-white/90'}>{info.subTitle}</p>

                    <Socials items={socials} />
                    <a
                        href={SBIS_RELIABILITY_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-3 inline-flex items-center gap-2 text-sm/6 text-white/80 hover:text-white"
                        aria-label="Рейтинг надёжности СБИС (откроется в новой вкладке)"
                    >
                        <span className="flex flex-col">
                            <span className="underline underline-offset-2">
                                Рейтинг надёжности СБИС
                            </span>
                            <span className="mt-1">
                                <img
                                    src="/img/sbic.png"
                                    alt="Виджет рейтинга надёжности"
                                    className="h-auto w-60 sm:w-72"
                                    loading="lazy"
                                />
                            </span>
                        </span>
                    </a>
                </div>

                {sections.map((section, sectionIdx) => {
                    if (section.id === 'catalog') {
                        const half = Math.ceil((section.links?.length || 0) / 2)
                        const first = section.links.slice(0, half)
                        const second = section.links.slice(half)
                        return (
                            <div key={sectionIdx} className="lg:col-span-2">
                                <h3 className="text-sm/6 font-semibold text-white">{section.title}</h3>
                                <div className="mt-4 grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2">
                                    <ul role="list" className="space-y-4">
                                        {first.map((link, linkIdx) => (
                                            <li className="group" key={link.id || linkIdx}>
                                                <a
                                                    href={localizeAndRegionalizePath(link.href)}
                                                    className="text-sm/6 text-white/80 hover:text-white"
                                                >
                                                    {link.name}
                                                </a>
                                            </li>
                                        ))}
                                    </ul>
                                    <ul role="list" className="space-y-4">
                                        {second.map((link, linkIdx) => (
                                            <li className="group" key={link.id || linkIdx}>
                                                <a
                                                    href={localizeAndRegionalizePath(link.href)}
                                                    className="text-sm/6 text-white/80 hover:text-white"
                                                >
                                                    {link.name}
                                                </a>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        )
                    }
                    return (
                        <div key={sectionIdx}>
                            <h3 className="text-sm/6 font-semibold text-white">{section.title}</h3>
                            <ul role="list" className="mt-4 space-y-4">
                                {section.links.map((link, linkIdx) => (
                                    <li className="group" key={link.id || linkIdx}>
                                        <a
                                            href={localizeAndRegionalizePath(link.href)}
                                            className="text-sm/6 text-white/80 hover:text-white"
                                        >
                                            {link.name}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )
                })}
            </div>
            {/*
              Единственная краулабельная связка между региональными версиями.
              Переключатель региона в шапке — это <button> + router.push внутри
              диалога, который рендерится только открытым, поэтому московское
              дерево не получало ни одной входящей ссылки и жило на одном sitemap.
            */}
            <nav aria-label="Регионы" className="mt-10">
                <h3 className="text-sm font-semibold text-white/90">Регионы</h3>
                <ul role="list" className="mt-3 flex flex-col gap-3 md:flex-row md:gap-6">
                    {branchLinks.map(({code, name, href, isCurrent}) => (
                        <li key={code}>
                            <a
                                href={href}
                                onClick={() => rememberRegionChoice(code)}
                                aria-current={isCurrent ? 'page' : undefined}
                                className={
                                    isCurrent
                                        ? 'text-sm/6 font-semibold text-white'
                                        : 'text-sm/6 text-white/80 hover:text-white'
                                }
                            >
                                {name}
                            </a>
                        </li>
                    ))}
                </ul>
            </nav>
            <hr className="mt-10 border-t border-white/20"/>
            <div className={'flex flex-col md:flex-row md:flex-wrap justify-between my-8 gap-8'}>
                <small className={'mb-6 text-white'}> © {currentYear} {info.title}</small>
                <ul role="list" className="flex gap-3 flex-col md:flex-row md:flex-wrap min-w-0">
                    <li className="group">
                        <a
                            href="/sitemap.xml"
                            className="text-sm/6 text-white/80 hover:text-white"
                        >
                            Карта сайта
                        </a>
                    </li>
                    {legal.map((link, linkIdx) => (
                        <li className="group" key={linkIdx}>
                            <a
                                href={localizeAndRegionalizePath(link.href)}
                                className="text-sm/6 text-white/80 hover:text-white"
                            >
                                {link.name}
                            </a>
                        </li>
                    ))}
                    <li className="group">
                        {/* Атрибуция гео-базы DB-IP City Lite (CC BY 4.0) — обязательна при использовании */}
                        <a
                            href="https://db-ip.com"
                            target="_blank"
                            rel="noopener noreferrer nofollow"
                            className="text-sm/6 text-white/60 hover:text-white"
                        >
                            IP Geolocation by DB-IP
                        </a>
                    </li>
                </ul>
                <a
                    href="https://webgoodpeople.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm/6 text-white/80 hover:text-white"
                >
                    Создание и продвижение сайта WebGoodPeople.
                </a>
            </div>
        </>
    );
}

export default FooterCom;
